import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { KnowledgeRecommendationsService } from "@/services/knowledge/KnowledgeRecommendationsService";

const LIST_KEY = "knowledge-recommendations";

/**
 * Pending Knowledge Genius recommendations, optionally scoped to one Genie.
 * Accepting a source change kicks off a server-side refresh of the document
 * (re-extract -> optimize -> ElevenLabs), so the list invalidates alongside
 * documents.
 */
export const useKnowledgeRecommendations = (agentId?: string) => {
  const queryClient = useQueryClient();

  const query = useQuery({
    queryKey: [LIST_KEY, agentId ?? "all"],
    queryFn: () => KnowledgeRecommendationsService.list({ agentId }),
  });

  const invalidate = () => {
    void queryClient.invalidateQueries({ queryKey: [LIST_KEY] });
    void queryClient.invalidateQueries({ queryKey: ["documents"] });
  };

  const acceptMutation = useMutation({
    mutationFn: (recommendationId: string) =>
      KnowledgeRecommendationsService.accept(recommendationId),
    onSuccess: (result) => {
      toast.success(
        result?.refreshed
          ? "Updating your Genie's knowledge from the new content"
          : "Recommendation accepted",
      );
      invalidate();
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to apply the update");
    },
  });

  const dismissMutation = useMutation({
    mutationFn: (recommendationId: string) =>
      KnowledgeRecommendationsService.dismiss(recommendationId),
    onSuccess: () => invalidate(),
    onError: (error: Error) => {
      toast.error(error.message || "Failed to dismiss the recommendation");
    },
  });

  return {
    recommendations: query.data || [],
    isLoading: query.isLoading,
    accept: acceptMutation.mutate,
    acceptingId: acceptMutation.isPending ? acceptMutation.variables : null,
    dismiss: dismissMutation.mutate,
    dismissingId: dismissMutation.isPending ? dismissMutation.variables : null,
  };
};
